import React, { memo } from "react";
import { Link } from "react-router-dom";

/**
 * Shared card for the results list and the single-result detail views.
 *
 * Layout: an optional header row — leading icon + title justified left, with
 * `meta` (date/time or any short info) on the right — then the card body
 * (`children`) on its own full-width row beneath. Keeping the icon in the header
 * row (rather than a left column beside the body) avoids the dead whitespace
 * under the icon that a two-column card leaves when the body is tall.
 *
 * Pass `to` to make the whole card a router link (used by the results list).
 *
 * @param {{
 *   icon?: React.ReactNode,
 *   title?: React.ReactNode,
 *   meta?: React.ReactNode,
 *   to?: string | null,
 *   children?: React.ReactNode,
 *   className?: string,
 * }} props
 */
function ResultCard({
  icon = null,
  title = null,
  meta = null,
  to = null,
  children = null,
  className = "",
}) {
  const hasHeader = Boolean(icon || title || meta);
  const hasBody = React.Children.count(children) > 0;

  const content = (
    <>
      {hasHeader ? (
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {icon ? (
              <span
                data-testid="result-card-icon-wrapper"
                className="flex h-[22px] w-[22px] text-[22px] shrink-0 items-center justify-center text-primary [&_svg]:h-[22px] [&_svg]:w-auto [&_svg]:fill-current [&_svg]:stroke-current"
                aria-hidden="true"
              >
                {icon}
              </span>
            ) : null}
            {title ? (
              <h3 className="truncate text-sm font-semibold text-typography">
                {title}
              </h3>
            ) : null}
          </div>
          {meta ? (
            <span className="shrink-0 muted-text text-sm">{meta}</span>
          ) : null}
        </div>
      ) : null}
      {hasBody ? <div className="w-full">{children}</div> : null}
    </>
  );

  const cardClassName =
    `card w-full p-3 flex flex-col gap-3 text-left ${className}`.trim();

  return to ? (
    <Link to={to} className={cardClassName}>
      {content}
    </Link>
  ) : (
    <div className={cardClassName}>{content}</div>
  );
}

export default memo(ResultCard);
